import { useEffect, useState } from "react";
import axios from "axios";
import { Lock, RefreshCw, Search, Mail, Phone, Building2, Calendar, X, Filter, Download } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_META = {
  contact: { label: "Contact", tone: "bg-slate-100 text-slate-800 border-slate-200" },
  enquiry: { label: "Enquiry", tone: "bg-blue-50 text-[#0055FF] border-blue-200" },
  quote: { label: "Quote", tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  career: { label: "Career", tone: "bg-amber-50 text-amber-700 border-amber-200" },
};

const AdminInbox = () => {
  const [password, setPassword] = useState(() => sessionStorage.getItem("aar_admin_pwd") || "");
  const [inputPwd, setInputPwd] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authed, setAuthed] = useState(false);
  const [kind, setKind] = useState("all");
  const [query, setQuery] = useState("");
  const [detail, setDetail] = useState(null);

  const fetchLeads = async (pwd, k = kind) => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/leads`,
        { kind: k === "all" ? null : k },
        { headers: { "X-Admin-Password": pwd } }
      );
      setLeads(data);
      setAuthed(true);
      sessionStorage.setItem("aar_admin_pwd", pwd);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Invalid admin password");
        sessionStorage.removeItem("aar_admin_pwd");
        setAuthed(false);
        setPassword("");
      } else toast.error("Failed to load submissions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (password) fetchLeads(password, kind); /* eslint-disable-next-line */ }, []);
  useEffect(() => { if (authed) fetchLeads(password, kind); /* eslint-disable-next-line */ }, [kind]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (!inputPwd.trim()) return;
    setPassword(inputPwd.trim());
    fetchLeads(inputPwd.trim(), kind);
  };

  const logout = () => {
    sessionStorage.removeItem("aar_admin_pwd");
    setAuthed(false); setPassword(""); setLeads([]);
  };

  const filtered = leads.filter((l) => {
    if (!query) return true;
    const q = query.toLowerCase();
    return [l.name, l.email, l.phone, l.company, l.message, l.machine, l.service, l.position]
      .filter(Boolean).some((v) => v.toLowerCase().includes(q));
  });

  const exportCsv = () => {
    const cols = ["created_at", "kind", "name", "email", "phone", "company", "machine", "service", "budget", "position", "experience", "subject", "message"];
    const rows = [cols.join(",")].concat(
      filtered.map((l) => cols.map((c) => `"${String(l[c] ?? "").replace(/"/g, '""')}"`).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `accurate-leads-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (!authed) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6 pt-24 pb-16">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white border border-slate-200 rounded-2xl p-8 shadow-sm" data-testid="admin-login">
          <div className="w-12 h-12 rounded-xl bg-[#0055FF]/10 border border-[#0055FF]/25 flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6 text-[#0055FF]" />
          </div>
          <h1 className="mt-4 font-display text-2xl text-slate-900 text-center">Admin Inbox</h1>
          <p className="mt-1 text-sm text-slate-500 text-center">Enter password to view submissions</p>
          <input
            autoFocus type="password" value={inputPwd} onChange={(e) => setInputPwd(e.target.value)}
            placeholder="Password"
            className="mt-6 w-full bg-white border border-slate-300 rounded-xl px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 outline-none focus:border-[#0055FF]"
            data-testid="admin-password-input"
          />
          <button type="submit" className="btn-primary w-full justify-center mt-4" data-testid="admin-login-btn">
            {loading ? "Verifying..." : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  const counts = leads.reduce((a, l) => ({ ...a, [l.kind]: (a[l.kind] || 0) + 1 }), {});
  const total = leads.length;

  return (
    <div className="min-h-screen bg-slate-50 pt-24 pb-16">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-[#0055FF] font-semibold">Private Dashboard</p>
            <h1 className="mt-1 font-display text-3xl text-slate-900">Submissions Inbox</h1>
            <p className="mt-1 text-sm text-slate-500">{total} total · showing {filtered.length}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => fetchLeads(password, kind)} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="admin-refresh">
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={exportCsv} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="admin-export">
              <Download className="w-3.5 h-3.5" /> Export CSV
            </button>
            <button onClick={logout} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="admin-logout">Log out</button>
          </div>
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2 mb-4" data-testid="admin-filters">
          {["all", "contact", "enquiry", "quote", "career"].map((k) => (
            <button key={k} onClick={() => setKind(k)}
              className={`px-4 py-1.5 rounded-full text-xs border transition-all ${kind === k ? "bg-[#0055FF] border-[#0055FF] text-white" : "bg-white border-slate-200 text-slate-700 hover:border-slate-400"}`}
              data-testid={`admin-filter-${k}`}>
              {k === "all" ? "All" : KIND_META[k]?.label} {k !== "all" && counts[k] ? `· ${counts[k]}` : ""}
            </button>
          ))}
          <div className="relative ml-auto w-full md:w-72">
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-slate-400" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, message..."
              className="w-full bg-white border border-slate-200 rounded-full pl-9 pr-4 py-2 text-xs text-slate-900 outline-none focus:border-[#0055FF]"
              data-testid="admin-search" />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="admin-leads-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                <tr>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Date</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Type</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Contact</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Company</th>
                  <th className="text-left px-4 py-3 font-medium text-xs uppercase tracking-wider">Subject / Item</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-16 text-slate-400 text-sm">No submissions found.</td></tr>
                )}
                {filtered.map((l) => {
                  const meta = KIND_META[l.kind] || KIND_META.contact;
                  return (
                    <tr key={l.id} className="border-b border-slate-100 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => setDetail(l)} data-testid={`admin-row-${l.id}`}>
                      <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{new Date(l.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider border font-semibold ${meta.tone}`}>{meta.label}</span></td>
                      <td className="px-4 py-3 font-medium text-slate-900">{l.name}</td>
                      <td className="px-4 py-3 text-slate-700">
                        <div className="flex flex-col gap-0.5">
                          <span className="text-xs">{l.email}</span>
                          {l.phone && <span className="text-xs text-slate-500">{l.phone}</span>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-xs">{l.company || "—"}</td>
                      <td className="px-4 py-3 text-slate-700 text-xs max-w-xs truncate">{l.machine || l.service || l.position || l.subject || (l.message ? l.message.slice(0, 60) + "…" : "—")}</td>
                      <td className="px-4 py-3 text-right text-[#0055FF] text-xs font-medium">View →</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Detail drawer */}
      {detail && (
        <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={() => setDetail(null)} data-testid="admin-detail">
          <div className="w-full max-w-md bg-white h-full overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <span className={`px-2.5 py-1 rounded-full text-xs border font-semibold ${(KIND_META[detail.kind] || KIND_META.contact).tone}`}>{(KIND_META[detail.kind] || KIND_META.contact).label}</span>
              <button onClick={() => setDetail(null)} className="text-slate-500 hover:text-slate-900"><X className="w-5 h-5" /></button>
            </div>
            <h2 className="font-display text-2xl text-slate-900">{detail.name}</h2>
            <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> {new Date(detail.created_at).toLocaleString()}</p>
            <div className="mt-6 space-y-3 text-sm">
              <a href={`mailto:${detail.email}`} className="flex items-center gap-2 text-slate-700 hover:text-[#0055FF]"><Mail className="w-4 h-4 text-[#0055FF]" /> {detail.email}</a>
              {detail.phone && <a href={`tel:${detail.phone}`} className="flex items-center gap-2 text-slate-700 hover:text-[#0055FF]"><Phone className="w-4 h-4 text-[#0055FF]" /> {detail.phone}</a>}
              {detail.company && <p className="flex items-center gap-2 text-slate-700"><Building2 className="w-4 h-4 text-[#0055FF]" /> {detail.company}</p>}
            </div>
            <dl className="mt-6 space-y-3 text-sm">
              {["machine", "service", "budget", "position", "experience", "subject"].map((k) => detail[k] ? (
                <div key={k} className="grid grid-cols-3 gap-2 py-2 border-b border-slate-100">
                  <dt className="text-xs uppercase tracking-wider text-slate-500 capitalize">{k}</dt>
                  <dd className="col-span-2 text-slate-800">{detail[k]}</dd>
                </div>
              ) : null)}
              {detail.message && (
                <div className="pt-2">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">Message</p>
                  <p className="text-slate-800 leading-relaxed whitespace-pre-wrap">{detail.message}</p>
                </div>
              )}
            </dl>
            <div className="mt-8 flex gap-2">
              <a href={`mailto:${detail.email}`} className="btn-primary flex-1 justify-center !text-sm">Reply</a>
              {detail.phone && <a href={`tel:${detail.phone}`} className="btn-ghost flex-1 justify-center !text-sm">Call</a>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
